/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { MeasurementDataService } from '../../services/measurement-data.service';
import { Frame, CHART_COLORS } from '../../models/measurement-view.types';

// Register Chart.js components
Chart.register(...registerables);

@Component({
    selector: 'app-response-graph',
    templateUrl: './response-graph.component.html',
    styleUrls: ['./response-graph.component.scss'],
    standalone: false
})
export class ResponseGraphComponent implements AfterViewInit, OnDestroy {
    @ViewChild('responseCanvas', { static: true })
    public canvasRef!: ElementRef<HTMLCanvasElement>;

    private chart!: Chart;
    private destroy$ = new Subject<void>();
    private currentFrame: Frame | null = null;
    private maxAmplitude = 10;

    public canvasWidth = 800;
    public canvasHeight = 200;

    public constructor(private measurementDataService: MeasurementDataService) {}

    public ngAfterViewInit(): void {
        this.initializeChart();

        this.measurementDataService.selectedFrame$.pipe(takeUntil(this.destroy$)).subscribe((frame) => {
            this.currentFrame = frame;
            this.updateChart();
        });

        this.canvasRef.nativeElement.addEventListener('mouseleave', () => {
            this.measurementDataService.setHoveredWaveIndex(null);
        });
    }

    public ngOnDestroy(): void {
        this.measurementDataService.setHoveredWaveIndex(null);
        this.destroy$.next();
        this.destroy$.complete();
        if (this.chart) {
            this.chart.destroy();
        }
    }

    public scaleUp(): void {
        if (this.maxAmplitude > 5) {
            this.maxAmplitude += 3;
        } else if (this.maxAmplitude > 1) {
            this.maxAmplitude += 1;
        } else {
            this.maxAmplitude += 0.1;
        }
        this.updateChartScale();
    }

    public scaleDown(): void {
        if (this.maxAmplitude > 5) {
            this.maxAmplitude -= 3;
        } else if (this.maxAmplitude > 1) {
            this.maxAmplitude -= 1;
        } else {
            this.maxAmplitude -= 0.1;
        }
        if (this.maxAmplitude < 0.1) {
            this.maxAmplitude = 0.1;
        }
        this.updateChartScale();
    }

    private initializeChart(): void {
        // Custom plugin for drawing waveforms and peak/valley indicators (like original)
        const responseGraphPlugin = {
            id: 'responseGraphPlugin',
            afterDraw: (chart: Chart) => {
                const ctx = chart.ctx;
                const xScale = chart.scales['x'];
                const yScale = chart.scales['y'];

                // Draw text labels
                ctx.font = '11px Arial';
                ctx.fillStyle = CHART_COLORS.TIMELINE;
                ctx.fillText('Time (ms)', xScale.getPixelForDecimal(1) - 10, yScale.getPixelForDecimal(0) + 18);
            },
            beforeDatasetsDraw: (chart: Chart) => {
                const ctx = chart.ctx;
                const yScale = chart.scales['y'];
                const x_range = chart.chartArea.width;

                if (this.currentFrame) {
                    // Real data highlight areas
                    const frame = this.currentFrame;
                    const pixels_per_response = x_range / frame.wave_data.waves.length;

                    let areaIdx = 0;
                    for (const wave of frame.wave_data.waves) {
                        if (!wave.noise && wave.signal) {
                            // Calculate highlight area from 0 to peak-to-valley amplitude
                            const waveformCenterX =
                                chart.chartArea.left + areaIdx * pixels_per_response + pixels_per_response * 0.5;

                            // Make smaller rectangles around the waveform area
                            const highlightWidth = pixels_per_response * 0.8;
                            const highlightLeft = waveformCenterX - highlightWidth * 0.5;

                            // Use actual peak-to-valley amplitude for rectangle height
                            const amplitude = wave.peak_value - wave.valley_value;

                            // Calculate rectangle from 0 line to amplitude height
                            const zeroY = yScale.getPixelForValue(0); // Y position for 0 mV
                            const amplitudeY = yScale.getPixelForValue(amplitude); // Y position for amplitude

                            const highlightTop = Math.min(zeroY, amplitudeY); // Top of rectangle
                            const highlightHeight = Math.abs(zeroY - amplitudeY); // Height from 0 to amplitude

                            // Subtle highlight areas like in original
                            ctx.fillStyle = 'rgba(240, 240, 240, 0.4)';
                            ctx.fillRect(highlightLeft, highlightTop, highlightWidth, highlightHeight);

                            // Subtle border
                            ctx.strokeStyle = 'rgba(200, 200, 200, 0.6)';
                            ctx.lineWidth = 1;
                            ctx.strokeRect(highlightLeft, highlightTop, highlightWidth, highlightHeight);
                        }
                        areaIdx++;
                    }
                }
            },
            afterDatasetDraw: (chart: Chart) => {
                const ctx = chart.ctx;
                const yScale = chart.scales['y'];

                const top_y = yScale.top;
                const bot_y = yScale.bottom;
                const diff_y = bot_y - top_y;

                const max_mv = this.maxAmplitude;
                const min_mv = -this.maxAmplitude;
                const span_mv = max_mv - min_mv;

                const x_range = chart.chartArea.width;

                if (this.currentFrame) {
                    const frame = this.currentFrame;
                    const pixels_per_response = x_range / frame.wave_data.waves.length;
                    const pixels_per_sample = pixels_per_response / (frame.wave_data.waves[0]?.data.length - 1 || 1);

                    // Draw peak and valley indicators
                    ctx.beginPath();
                    ctx.lineWidth = 2;
                    ctx.strokeStyle = CHART_COLORS.TIMELINE;

                    let idx = 0;
                    for (const wave of frame.wave_data.waves) {
                        if (!wave.noise && wave.signal) {
                            // Draw the peak circle
                            const peakPerc = (wave.peak_value - min_mv) / span_mv;
                            const peakYVal = bot_y - peakPerc * diff_y;
                            const peakXVal =
                                chart.chartArea.left + idx * pixels_per_response + pixels_per_sample * wave.peak_index;
                            ctx.moveTo(peakXVal + 4, peakYVal);
                            ctx.arc(peakXVal, peakYVal, 4, 0, 2 * Math.PI);

                            // Draw the valley cross
                            const valleyPerc = (wave.valley_value - min_mv) / span_mv;
                            const valleyYVal = bot_y - valleyPerc * diff_y;
                            const valleyXVal =
                                chart.chartArea.left +
                                idx * pixels_per_response +
                                pixels_per_sample * wave.valley_index;
                            ctx.moveTo(valleyXVal - 3, valleyYVal - 3);
                            ctx.lineTo(valleyXVal + 3, valleyYVal + 3);
                            ctx.moveTo(valleyXVal + 3, valleyYVal - 3);
                            ctx.lineTo(valleyXVal - 3, valleyYVal + 3);
                        }
                        idx++;
                    }
                    ctx.stroke();

                    // Draw the waves
                    ctx.beginPath();
                    ctx.lineWidth = 1;
                    ctx.strokeStyle = CHART_COLORS.TIMELINE;
                    let waveXOffset = 0;

                    for (const wave of frame.wave_data.waves) {
                        let idx = 0;
                        let prevx, prevy;
                        for (const val of wave.data) {
                            const norm = (val - min_mv) / span_mv;
                            const y = bot_y - norm * diff_y;
                            const x = chart.chartArea.left + waveXOffset + idx * pixels_per_sample;
                            if (idx && prevx !== undefined && prevy !== undefined) {
                                ctx.moveTo(prevx, prevy);
                                ctx.lineTo(x, y);
                            }
                            prevx = x;
                            prevy = y;
                            idx++;
                        }
                        waveXOffset += pixels_per_response;
                    }
                    ctx.stroke();
                }
            }
        };

        const config: ChartConfiguration = {
            type: 'bar' as ChartType,
            data: {
                datasets: [
                    {
                        label: 'Peak-to-Valley Amplitude',
                        data: [0, 0, 0, 0],
                        backgroundColor: 'transparent', // Make bars invisible but still interactive
                        borderColor: 'transparent',
                        borderWidth: 0,
                        categoryPercentage: 1.0,
                        barPercentage: 0.8 // Make bars wider for easier hovering
                    }
                ]
            },
            plugins: [responseGraphPlugin],
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                layout: {
                    padding: { left: 30, right: 60, top: 30, bottom: 20 }
                },
                scales: {
                    x: {
                        ticks: {
                            font: { family: 'Arial', size: 10 },
                            color: CHART_COLORS.AMPLITUDE
                        }
                    },
                    y: {
                        type: 'linear',
                        max: this.maxAmplitude,
                        min: -this.maxAmplitude,
                        ticks: {
                            count: 7,
                            font: { family: 'Arial', size: 10 },
                            color: CHART_COLORS.AMPLITUDE,
                            callback: (value) =>
                                this.measurementDataService.roundValue(
                                    Number(value),
                                    Math.abs(Number(value)) >= 100 ? 0 : 2
                                )
                        }
                    }
                },
                onHover: (_, elements) => {
                    this.measurementDataService.setHoveredWaveIndex(elements[0]?.index ?? null);
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: true,
                        mode: 'index',
                        intersect: false,
                        backgroundColor: CHART_COLORS.AMPLITUDE,
                        borderColor: CHART_COLORS.AMPLITUDE,
                        borderWidth: 1,
                        displayColors: false,
                        titleColor: '#000',
                        bodyColor: '#000',
                        position: 'nearest',
                        callbacks: {
                            title: () => 'Amplitude (mV)',
                            label: (context) => {
                                if (!this.currentFrame) return '';

                                const waves = this.currentFrame.wave_data.waves;
                                const waveIndex = context.dataIndex;
                                const wave = waves[waveIndex];

                                if (!wave || wave.noise || !wave.signal) {
                                    return ['Peak-to-Valley: -', 'Peak: -', 'Valley: -'];
                                }

                                const amplitude = this.measurementDataService.roundValue(
                                    wave.peak_value - wave.valley_value,
                                    2
                                );
                                const peak = this.measurementDataService.roundValue(wave.peak_value, 2);
                                const valley = this.measurementDataService.roundValue(wave.valley_value, 2);

                                return [`Peak-to-Valley: ${amplitude} mV`, `Peak: ${peak} mV`, `Valley: ${valley} mV`];
                            }
                        }
                    }
                }
            }
        };

        this.chart = new Chart(this.canvasRef.nativeElement, config);
    }

    private updateChart(): void {
        if (!this.chart) return;

        let barData: number[] = [];

        if (this.currentFrame) {
            const responseData = this.measurementDataService.getResponseGraphData(this.currentFrame);
            if (!responseData) return;

            // Auto-scale Y axis to fit the data
            this.autoScaleFromFrame();

            // Update bar chart data
            barData = responseData.waves.map((wave) => {
                if (wave.noise || !wave.signal) {
                    return 0;
                }

                const amplitude = this.measurementDataService.roundValue(wave.peak_value - wave.valley_value, 2);
                return amplitude;
            });
        }

        this.chart.data.datasets[0].data = barData;

        // Create labels for x-axis showing time ranges in milliseconds
        // PTC responses are 1000ms apart, TOF responses are 500ms apart
        const responseType = this.currentFrame?.frame_type;
        const isPTC = responseType === 'PTC';
        let time_ms = 0;
        const labels = barData.map((_, index) => {
            const waveLength = this.currentFrame?.wave_data?.waves[index]?.data.length || 0;
            const label = `${time_ms} - ${time_ms + waveLength}`;
            time_ms += isPTC ? 1000 : 500;
            return label;
        });
        this.chart.data.labels = labels;

        // Enable tooltips now that we have data
        if (this.chart.options.plugins?.tooltip) {
            this.chart.options.plugins.tooltip.enabled = true;
        }

        this.chart.update();
    }

    private autoScaleFromFrame(): void {
        const measurement = this.measurementDataService.getCurrentMeasurement();
        if (!measurement) return;

        const frames = measurement.data.filter((entry): entry is Frame => entry.type === 'frame');
        const maxAmpl = this.measurementDataService.getMaxAmplitude(frames);
        if (maxAmpl > 0) {
            this.maxAmplitude = Math.ceil(maxAmpl) + 1;
            this.updateChartScale();
        }
    }

    private updateChartScale(): void {
        if (!this.chart) return;

        if (this.chart.options.scales?.['y']) {
            this.chart.options.scales['y'].max = this.maxAmplitude;
            this.chart.options.scales['y'].min = -this.maxAmplitude;
        }

        this.chart.update();
    }
}
